/**
 * NPPES NPI Registry Service
 * Integrates with the CMS NPPES NPI Registry API v2.1
 * https://npiregistry.cms.hhs.gov/api-page
 */

// Types for NPI Registry API
export interface NPISearchParams {
  // Required parameters
  version?: string; // API version, defaults to "2.1"
  
  // Search parameters - at least one is required
  number?: string; // 10-digit NPI number
  first_name?: string;
  last_name?: string;
  organization_name?: string;
  
  // Location parameters
  address_purpose?: 'LOCATION' | 'MAILING';
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
  
  // Filter parameters
  taxonomy_description?: string;
  enumeration_type?: 'NPI-1' | 'NPI-2'; // NPI-1 for individuals, NPI-2 for organizations
  
  // Result parameters
  limit?: number; // Maximum 200, default 10
  skip?: number; // Number of results to skip for pagination
  pretty?: boolean; // Pretty print JSON response
}

export interface NPIAddress {
  country_code: string;
  country_name: string;
  address_purpose: string;
  address_type: string;
  address_1: string;
  address_2?: string;
  city: string;
  state: string;
  postal_code: string;
  telephone_number?: string;
  fax_number?: string;
}

export interface NPITaxonomy {
  code: string;
  taxonomy_group: string;
  desc: string;
  state?: string;
  license?: string;
  primary: boolean;
}

export interface NPIIdentifier {
  code: string;
  desc: string;
  identifier: string;
  state?: string;
  issuer?: string;
}

export interface NPIEndpoint {
  endpoint_type: string;
  endpoint_type_description: string;
  endpoint: string;
  endpoint_description?: string;
  affiliation?: string;
  use?: string;
  content_type?: string;
  country_code?: string;
  country_name?: string;
  address_type?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

export interface NPIBasicInfo {
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  credential?: string;
  sole_proprietor?: string;
  gender?: string;
  enumeration_date?: string;
  last_updated?: string;
  status?: string;
  name?: string; // Organization name
  name_prefix?: string;
  name_suffix?: string;
  authorized_official_credential?: string;
  authorized_official_first_name?: string;
  authorized_official_last_name?: string;
  authorized_official_middle_name?: string;
  authorized_official_name_prefix?: string;
  authorized_official_name_suffix?: string;
  authorized_official_telephone_number?: string;
  authorized_official_title_or_position?: string;
}

export interface NPIProvider {
  number: string;
  enumeration_type: string;
  basic: NPIBasicInfo;
  other_names?: any[];
  identifiers?: NPIIdentifier[];
  taxonomies?: NPITaxonomy[];
  addresses?: NPIAddress[];
  practice_locations?: NPIAddress[];
  endpoints?: NPIEndpoint[];
  created_epoch: number;
  last_updated_epoch: number;
}

export interface NPISearchResponse {
  result_count: number;
  results: NPIProvider[];
}

export interface NPIError {
  field?: string;
  description: string;
}

export interface NPIErrorResponse {
  Errors: NPIError[];
}

class NPIRegistryService {

  private readonly baseUrl = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';
  private readonly npiSearchPath = `${this.baseUrl}/facility/npi/search`;
  private readonly defaultVersion = '2.1';
  private readonly maxRetries = 3;
  private readonly timeoutMs = 10000;

  /**
   * Search for providers using the NPPES NPI Registry API via proxy
   */
  async searchProviders(params: NPISearchParams, idToken: string): Promise<NPISearchResponse> {
    if (!idToken) throw new Error('JWT token is required for NPI Registry API');
    try {
      const queryParams = this.buildQueryParams(params);
      const url = `${this.npiSearchPath}?${queryParams}`;

      const response = await this.fetchWithRetry(url, idToken);
      
      if (!response.ok) {
        const errorData = await response.json() as NPIErrorResponse;
        throw new Error(`NPI API Error: ${this.formatErrors(errorData.Errors)}`);
      }

      const data = await response.json() as NPISearchResponse;
      return data;
    } catch (error: any) {
      console.error('Error searching NPI providers:', error);
      throw error;
    }
  }

  /**
   * Search for a provider by NPI number
   */
  async getProviderByNPI(npiNumber: string, idToken: string): Promise<NPIProvider | null> {
    if (!idToken) throw new Error('JWT token is required for NPI Registry API');
    if (!this.isValidNPI(npiNumber)) {
      throw new Error('Invalid NPI number format');
    }

    const response = await this.searchProviders({ number: npiNumber }, idToken);
    return response.results.length > 0 ? response.results[0] : null;
  }

  /**
   * Search for providers by name
   */
  async searchProvidersByName(
    idToken: string,
    firstName?: string, 
    lastName?: string, 
    organizationName?: string
  ): Promise<NPIProvider[]> {
    if (!idToken) throw new Error('JWT token is required for NPI Registry API');
    if (!firstName && !lastName && !organizationName) {
      throw new Error('At least one name parameter is required');
    }

    const params: NPISearchParams = {
      limit: 50
    };

    if (firstName) params.first_name = firstName;
    if (lastName) params.last_name = lastName;
    if (organizationName) params.organization_name = organizationName;

    const response = await this.searchProviders(params, idToken);
    return response.results;
  }

  /**
   * Search for providers by location
   */
  async searchProvidersByLocation(
    idToken: string,
    city?: string, 
    state?: string, 
    postalCode?: string, 
    taxonomyDescription?: string
  ): Promise<NPIProvider[]> {
    if (!idToken) throw new Error('JWT token is required for NPI Registry API');
    if (!city && !state && !postalCode) {
      throw new Error('At least one location parameter is required');
    }

    const params: NPISearchParams = {
      limit: 50
    };

    if (city) params.city = city;
    if (state) params.state = state;
    if (postalCode) params.postal_code = postalCode;
    if (taxonomyDescription) params.taxonomy_description = taxonomyDescription;

    const response = await this.searchProviders(params, idToken);
    return response.results;
  }

  /**
   * Search for providers by specialty/taxonomy
   */
  async searchProvidersBySpecialty(idToken: string, taxonomy: string, state?: string): Promise<NPIProvider[]> {
    if (!idToken) throw new Error('JWT token is required for NPI Registry API');
    const params: NPISearchParams = {
      taxonomy_description: taxonomy,
      limit: 50
    };

    if (state) params.state = state;

    const response = await this.searchProviders(params, idToken);
    return response.results;
  }

  /**
   * Get a formatted display name for a provider
   */
  getProviderDisplayName(provider: NPIProvider): string {
    if (provider.enumeration_type === 'NPI-1') {
      // Individual provider
      const { first_name, last_name, credential } = provider.basic;
      let name = '';
      
      if (first_name) name += first_name;
      if (last_name) name += (name ? ' ' : '') + last_name;
      if (credential) name += `, ${credential}`;
      
      return name || 'Unknown Provider';
    } else {
      // Organization provider
      return provider.basic.name || 'Unknown Organization';
    }
  }

  /**
   * Get the primary address for a provider
   */
  getPrimaryAddress(provider: NPIProvider): NPIAddress | null {
    const addresses = provider.addresses || [];
    const primaryAddress = addresses.find(addr => addr.address_purpose === 'LOCATION');
    return primaryAddress || (addresses.length > 0 ? addresses[0] : null);
  }

  /**
   * Get the primary taxonomy for a provider
   */
  getPrimaryTaxonomy(provider: NPIProvider): NPITaxonomy | null {
    const taxonomies = provider.taxonomies || [];
    const primary = taxonomies.find(tax => tax.primary);
    return primary || (taxonomies.length > 0 ? taxonomies[0] : null);
  }

  /**
   * Format address as a string
   */
  formatAddress(address: NPIAddress): string {
    let formatted = '';
    
    if (address.address_1) formatted += address.address_1;
    if (address.address_2) formatted += (formatted ? ', ' : '') + address.address_2;
    if (address.city) formatted += (formatted ? ', ' : '') + address.city;
    if (address.state) formatted += (formatted ? ', ' : '') + address.state;
    if (address.postal_code) formatted += (formatted ? ' ' : '') + address.postal_code;
    
    return formatted;
  }

  /**
   * Validate NPI number format
   */
  private isValidNPI(npi: string): boolean {
    // NPI should be 10 digits
    const npiRegex = /^\d{10}$/;
    return npiRegex.test(npi);
  }

  /**
   * Build query parameters for the API request
   */
  private buildQueryParams(params: NPISearchParams): string {
    const queryParams = new URLSearchParams();
    
    // Set default version if not provided
    queryParams.append('version', params.version || this.defaultVersion);
    
    // Add all search parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== 'version') {
        queryParams.append(key, value.toString());
      }
    });

    return queryParams.toString();
  }

  /**
   * Fetch with retry logic and authentication
   */
  private async fetchWithRetry(url: string, idToken: string, retries = this.maxRetries): Promise<Response> {
    if (!idToken) throw new Error('JWT token is required for NPI Registry API');
    for (let i = 0; i < retries; i++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

        const headers: HeadersInit = {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        };

        const response = await fetch(url, {
          signal: controller.signal,
          headers
        });

        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        if (i === retries - 1) throw error;
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
    }
    throw new Error('Max retries exceeded');
  }

  /**
   * Format API errors for display
   */
  private formatErrors(errors: NPIError[]): string {
    return errors.map(error => 
      error.field ? `${error.field}: ${error.description}` : error.description
    ).join('; ');
  }
}

// Export singleton instance
export const npiRegistryService = new NPIRegistryService();
export default npiRegistryService; 